import { Undefinable } from '@symfa-inc/providence-verizon-types';
import { StoredRequest } from '@core/utils/store/stored-request-decorator';
import {
  DEFAULT_JURISDICTION_PERMITTING_PANEL_FIELDS,
  DEFAULT_JURISDICTION_ZONING_PANEL_FIELDS,
} from '@models/constants/default-jurisdiction-panel-fields';
import {
  FinancialData,
  JurisdictionBody,
  JurisdictionBrowse,
  JurisdictionDetailsContacts,
  JurisdictionDetailsPermitting,
  JurisdictionDetailsZoning,
  JurisdictionModalValues,
  JurisdictionOption,
  JurisdictionPermittingData,
  JurisdictionSummaryZoning,
  JurisdictionZoningData,
  MainBrowseFilter,
  PaginatedResponse,
  PermittingBrowseFilter,
  PermittingBrowseResponse,
  PermittingJurisdictionAuditBody,
  PermittingJurisdictionAuditData,
  PermittingSummaryData,
  PermittingWorkflowBody,
  PermittingWorkflowData,
  ProjectDetailsResponse,
} from '@models/interfaces';
import {
  BreadcrumbsActions,
  FinancialActions,
  JurisdictionActions,
  PermittingActions,
} from '@store/actions';
import {
  PermittingEffects,
  ProjectFuzeIDsEffects,
  ProjectsEffects,
} from '@store/effects';
import { CommonHttpService } from './common-http.service';

export class PermittingHttpService extends CommonHttpService {
  @StoredRequest(PermittingActions.getPermittingBrowseData)
  getPermittingBrowseData(
    params: PermittingBrowseFilter,
  ): Promise<PaginatedResponse<PermittingBrowseResponse>> {
    return this.http.callApi.get(`${this.apiUrl}/permitting`, null, { params });
  }

  async deleteJurisdiction(jurisdictionId: string): Promise<void> {
    await this.http.callApi.delete(
      `${this.apiUrl}/permitting/jurisdictions`,
      null,
      {
        params: { jurisdictionId },
      },
    );
  }

  @StoredRequest(PermittingActions.getJurisdictionList)
  getJurisdictionList(): Promise<JurisdictionOption[]> {
    return this.http.callApi.get(
      `${this.apiUrl}/permitting/jurisdictions/list-options`,
    );
  }

  @StoredRequest(
    PermittingActions.getPermittingDetailsData,
    BreadcrumbsActions.Permitting.permitting,
  )
  async getPermittingDetailsData(
    projectBundleID: string,
  ): Promise<ProjectDetailsResponse> {
    const response = await this.http.callApi.get(
      `${this.apiUrl}/permitting/${projectBundleID}`,
    );

    ProjectsEffects.setHoldOptions({
      holdReason: response.holdReason,
      projectStatus: { id: response.projectStatus, value: '' },
    } as ProjectDetailsResponse);

    return PermittingEffects.setFinancial(
      ProjectsEffects.setCurrentProvidenceAgent(
        ProjectsEffects.setPaceTaskFields(
          ProjectsEffects.setPaceTasks(
            ProjectFuzeIDsEffects.setProjectFuzeIDsFromDetails(response),
          ),
        ),
      ),
    );
  }

  @StoredRequest(
    PermittingActions.getSummaryTabData,
    BreadcrumbsActions.Permitting.summary,
  )
  async getPermittingSummaryData(
    projectBundleID: string,
  ): Promise<PermittingSummaryData> {
    const response: PermittingSummaryData = await this.http.callApi.get(
      `${this.apiUrl}/permitting/${projectBundleID}/summary`,
    );

    ProjectFuzeIDsEffects.setProjectFuzeIDs(response.fuzeIDs);

    ProjectsEffects.setHoldOptions({
      holdReason: response.project.holdReason,
      projectStatus: { id: response.project.projectStatus, value: '' },
    } as ProjectDetailsResponse);

    PermittingEffects.setCurrentProvidenceAgent(
      PermittingEffects.setPaceTaskFields(
        PermittingEffects.setPaceTasks(response),
      ),
    );

    return response;
  }

  @StoredRequest(PermittingActions.getWorkflowTabData)
  async getPermittingWorkflowData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<PermittingWorkflowData> {
    const response: PermittingWorkflowData = await this.http.callApi.get(
      `${this.apiUrl}/permitting/${projectBundleID}/workflow`,
    );

    return response;
  }

  @StoredRequest(PermittingActions.getJurisdictionAuditTabData)
  async getPermittingJurisdictionAuditData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<PermittingJurisdictionAuditData> {
    const response: PermittingJurisdictionAuditData =
      await this.http.callApi.get(
        `${this.apiUrl}/permitting/${projectBundleID}/jurisdiction-audit`,
      );

    return response;
  }

  @StoredRequest(PermittingActions.getContactsTabData)
  async getPermittingContactsData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<JurisdictionDetailsContacts[]> {
    const response: JurisdictionDetailsContacts[] = await this.http.callApi.get(
      `${this.apiUrl}/permitting/${projectBundleID}/contacts`,
    );

    return response;
  }

  @StoredRequest(
    FinancialActions.getFinancialDataAction,
    PermittingActions.getFinancialTabData,
  )
  async getPermittingFinancialData(
    projectBundleID: string,
    _options?: { silent?: boolean },
  ): Promise<FinancialData> {
    const response: FinancialData = await this.http.callApi.get(
      `${this.apiUrl}/permitting/${projectBundleID}/financial`,
    );

    return response;
  }

  @StoredRequest(
    PermittingActions.getPermittingDetailsData,
    BreadcrumbsActions.Permitting.permitting,
  )
  getPermittingForLeasisingJurisdictionAuditTab(
    projectBundleID: string,
  ): Promise<ProjectDetailsResponse> {
    return this.http.callApi.get(
      `${this.apiUrl}/permitting/${projectBundleID}`,
    );
  }

  @StoredRequest(PermittingActions.permittingUpdateWorkflowData)
  async updatePermittingWorkflowData(
    projectId: string,
    body: PermittingWorkflowBody,
  ): Promise<PermittingWorkflowBody> {
    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/${projectId}/workflow`,
      body,
    );

    return body;
  }

  @StoredRequest(PermittingActions.permittingUpdateJurisdictionAuditData)
  async updatePermittingJurisdictionAuditData(
    projectId: string,
    body: PermittingJurisdictionAuditBody,
  ): Promise<PermittingJurisdictionAuditBody> {
    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/${projectId}/jurisdiction-audit`,
      body,
    );

    return body;
  }

  // PERMITTING - JURISDICTION
  @StoredRequest(JurisdictionActions.getJurisdictionBrowseData)
  getJurisdictionBrowseData(
    params: MainBrowseFilter,
  ): Promise<PaginatedResponse<JurisdictionBrowse>> {
    return this.http.callApi.get(
      `${this.apiUrl}/permitting/jurisdictions`,
      null,
      { params },
    );
  }

  @StoredRequest(
    JurisdictionActions.getJurisdictionZoning,
    BreadcrumbsActions.Permitting.jurisdiction,
  )
  getJurisdictionZoning(
    jurisdictionNameAndState: string,
  ): Promise<JurisdictionSummaryZoning> {
    return this.http.callApi.get(
      `${this.apiUrl}/permitting/jurisdictions/${jurisdictionNameAndState}/zoning`,
    );
  }

  @StoredRequest(
    JurisdictionActions.getJurisdictionPermitting,
    BreadcrumbsActions.Permitting.jurisdiction,
  )
  getJurisdictionPermitting(
    jurisdictionNameAndState: string,
    _options?: { silent?: boolean },
  ): Promise<JurisdictionDetailsPermitting[]> {
    return this.http.callApi.get(
      `${this.apiUrl}/permitting/jurisdictions/${jurisdictionNameAndState}/permitting`,
    );
  }

  @StoredRequest(JurisdictionActions.getJurisdictionContacts)
  getJurisdictionContacts(
    jurisdictionNameAndState: string,
    _options?: { silent?: boolean },
  ): Promise<JurisdictionDetailsContacts[]> {
    return this.http.callApi.get(
      `${this.apiUrl}/permitting/jurisdictions/${jurisdictionNameAndState}/contacts`,
    );
  }

  getJurisdictionNameValidation(
    id: Undefinable<string>,
    stateId: Undefinable<string>,
    jurisdictionName: string,
  ): Promise<boolean> {
    return this.http.callApi.get(
      `${this.apiUrl}/permitting/jurisdictions/validation`,
      null,
      {
        params: { jurisdictionName: jurisdictionName.trim(), id, stateId },
      },
    );
  }

  @StoredRequest(JurisdictionActions.addJurisdiction)
  addJurisdiction(
    params: MainBrowseFilter,
    body: JurisdictionBody,
  ): Promise<PaginatedResponse<JurisdictionBrowse>> {
    return this.http.callApi.post(
      `${this.apiUrl}/permitting/jurisdictions`,
      body,
      { params },
    );
  }

  @StoredRequest(JurisdictionActions.addJurisdictionZoningPanel)
  async addJurisdictionZoningPanel(
    jurisdictionId: string,
    body: Omit<JurisdictionModalValues, 'id'>,
  ): Promise<JurisdictionDetailsZoning> {
    const id = await this.http.callApi.post(
      `${this.apiUrl}/permitting/jurisdictions/${jurisdictionId}/zoning-panel`,
      body,
    );

    return {
      id,
      ...body,
      ...DEFAULT_JURISDICTION_ZONING_PANEL_FIELDS,
    };
  }

  @StoredRequest(JurisdictionActions.updateJurisdictionZoning)
  async updateJurisdictionZoning(
    jurisdictionId: string,
    body: JurisdictionZoningData,
  ): Promise<JurisdictionZoningData> {
    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/jurisdictions/${jurisdictionId}/zoning`,
      body,
    );

    return body;
  }

  @StoredRequest(JurisdictionActions.updateJurisdictionZoningPanel)
  async updateJurisdictionZoningPanel(
    values: JurisdictionModalValues,
  ): Promise<JurisdictionModalValues> {
    const { id, ...body } = values;

    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/jurisdictions/zoning-panel`,
      body,
      { params: { zoningPanelId: id } },
    );

    return values;
  }

  @StoredRequest(JurisdictionActions.removeJurisdictionZoningPanel)
  async removeJurisdictionZoningPanel(zoningPanelId: string): Promise<string> {
    await this.http.callApi.delete(
      `${this.apiUrl}/permitting/jurisdictions/zoning-panel`,
      null,
      { params: { zoningPanelId } },
    );

    return zoningPanelId;
  }

  @StoredRequest(JurisdictionActions.addJurisdictionPermittingPanel)
  async addJurisdictionPermittingPanel(
    jurisdictionId: string,
    body: Omit<JurisdictionModalValues, 'id'>,
  ): Promise<JurisdictionDetailsPermitting> {
    const id = await this.http.callApi.post(
      `${this.apiUrl}/permitting/jurisdictions/${jurisdictionId}/permitting-panel`,
      body,
    );

    return {
      id,
      ...body,
      ...DEFAULT_JURISDICTION_PERMITTING_PANEL_FIELDS,
    };
  }

  @StoredRequest(JurisdictionActions.updateJurisdictionPermittingPanel)
  async updateJurisdictionPermittingPanel(
    values: JurisdictionModalValues,
  ): Promise<JurisdictionModalValues> {
    const { id, ...body } = values;

    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/jurisdictions/permitting-panel`,
      body,
      { params: { permittingPanelId: id } },
    );

    return values;
  }

  @StoredRequest(JurisdictionActions.removeJurisdictionPermittingPanel)
  async removeJurisdictionPermittingPanel(
    permittingPanelId: string,
  ): Promise<string> {
    await this.http.callApi.delete(
      `${this.apiUrl}/permitting/jurisdictions/permitting-panel`,
      null,
      { params: { permittingPanelId } },
    );

    return permittingPanelId;
  }

  @StoredRequest(JurisdictionActions.updateJurisdictionPermitting)
  async updateJurisdictionPermitting(
    jurisdictionId: string,
    body: JurisdictionPermittingData,
  ): Promise<JurisdictionPermittingData> {
    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/jurisdictions/${jurisdictionId}/permitting`,
      body,
    );

    return body;
  }

  @StoredRequest(JurisdictionActions.addJurisdictionContact)
  async addJurisdictionContact(
    jurisdictionId: string,
    body: Omit<JurisdictionDetailsContacts, 'id'>,
  ): Promise<JurisdictionDetailsContacts> {
    const id = await this.http.callApi.post(
      `${this.apiUrl}/permitting/jurisdictions/contacts/${jurisdictionId}`,
      body,
    );

    return {
      id,
      ...body,
    };
  }

  @StoredRequest(JurisdictionActions.updateJurisdictionContact)
  async updateJurisdictionContact(
    contactId: string,
    body: Omit<JurisdictionDetailsContacts, 'id'>,
  ): Promise<JurisdictionDetailsContacts> {
    await this.http.callApi.patch(
      `${this.apiUrl}/permitting/jurisdictions/contacts`,
      body,
      { params: { contactId } },
    );

    return {
      id: contactId,
      ...body,
    };
  }

  @StoredRequest(JurisdictionActions.removeJurisdictionContact)
  async removeJurisdictionContact(contactId: string): Promise<string> {
    await this.http.callApi.delete(
      `${this.apiUrl}/permitting/jurisdictions/contacts`,
      null,
      { params: { contactId } },
    );

    return contactId;
  }
}
